// BFF authentication. The backend owns the OIDC flow and the tokens; the browser only ever
// holds the session cookie. These helpers drive the backend's /api/auth/* endpoints — no
// tokens, no oidc-client, nothing sensitive in the browser.

export interface BffUser {
  isAuthenticated: boolean;
  name?: string;
  email?: string;
  userId?: string;
}

/**
 * Full-page navigation to the BFF login, which 302s to the auth server and back.
 *
 * The returnUrl must never be an auth interstitial/terminal page (/login, /no-permission):
 * the BFF redirects there after a SUCCESSFUL login, and /login immediately re-triggers login —
 * an infinite redirect loop. Callers pass the originally-requested page; we fall back to home.
 */
export function signIn(returnUrl?: string): void {
  let target = returnUrl ?? window.location.pathname + window.location.search;
  if (!target || target === '/login' || target.startsWith('/login?') || target === '/no-permission') {
    target = '/';
  }
  window.location.href = `/api/auth/login?returnUrl=${encodeURIComponent(target)}`;
}

/** Full-page navigation to the BFF logout (clears the session AND the auth-server session). */
export function signOut(): void {
  window.location.href = '/api/auth/logout';
}

/** Read auth status from the BFF (this call also primes the CSRF cookie). */
export async function getUser(): Promise<BffUser | null> {
  try {
    const r = await fetch('/api/auth/user', { credentials: 'include' });
    if (!r.ok) return null;
    return (await r.json()) as BffUser;
  } catch {
    return null;
  }
}

export async function isAuthenticated(): Promise<boolean> {
  const u = await getUser();
  return u?.isAuthenticated === true;
}
